import { useEffect, useRef, type RefObject } from 'react';

/**
 * Devolve o foco que o `inert` da lista tirou durante um refine.
 *
 * A lista fica `inert` enquanto um refine de página 1 está em voo (`refining`, ver
 * `use_server_filter.ts`). Um elemento focado que fica dentro de uma subárvore
 * inerte perde o foco para o `body`, e o navegador não o devolve quando o `inert`
 * sai. O primeiro refine sai sozinho 250 ms depois de a busca pousar (as facetas do
 * painel): quem acabou de acionar "Fixar" ou "Detalhes" pelo teclado caía no topo
 * da página.
 *
 * Guarda o último elemento focado DENTRO da lista e, quando o refine pousa, o foca
 * de novo se ele continua na lista e se ninguém pegou o foco nesse meio-tempo
 * (`activeElement` ainda é o `body`). Sair da lista para outro elemento esquece o
 * guardado: o refine disparado por um filtro do painel não puxa o foco de volta.
 */
export function useRefineFocus(refining: boolean): RefObject<HTMLDivElement | null> {
  const listRef = useRef<HTMLDivElement>(null);
  const last = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement) last.current = event.target;
    };
    // Só a perda pelo `inert` fica guardada: ela não tem destino (`relatedTarget`
    // nulo) e acontece com a lista já inerte. Ir para fora da lista, ou para lugar
    // nenhum (clique numa área sem foco), é escolha de quem usa.
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget;
      const left = next instanceof Node ? !list.contains(next) : !list.hasAttribute('inert');
      if (left) last.current = null;
    };
    list.addEventListener('focusin', onFocusIn);
    list.addEventListener('focusout', onFocusOut);
    return () => {
      list.removeEventListener('focusin', onFocusIn);
      list.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  useEffect(() => {
    if (refining) return;
    const target = last.current;
    const list = listRef.current;
    if (!target || !list?.contains(target)) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    target.focus({ preventScroll: true });
  }, [refining]);

  return listRef;
}
